import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useJobs } from '../hooks/useApi';

/**
 * This component handles dynamic meta tag generation and redirects
 * for job pages based on job code, job ID, or job title
 */
const JobMeta = () => {
  const { jobCode } = useParams();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get all jobs to find by code
  const { data: allJobs, isLoading: allJobsLoading, error: jobsError } = useJobs();
  
  // This will run when the component mounts and add meta tags directly to head
  useEffect(() => {
    // Function to add meta tags directly to head
    const addMetaTags = (job) => {
      if (!job) return;
      
      // Remove any existing dynamic meta tags
      document.querySelectorAll('meta[data-dynamic="true"]').forEach(el => el.remove());
      document.querySelectorAll('script[data-dynamic="true"]').forEach(el => el.remove());
      
      // Set the title directly
      document.title = `${job.title} | ${job.company_name} | Fresher Job Adda`;
      
      // Helper function to create and append meta tags
      const createMeta = (property, content) => {
        const meta = document.createElement('meta');
        meta.setAttribute('data-dynamic', 'true');
        
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      };
      
      // Set basic meta tags
      createMeta('description', `${job.title} at ${job.company_name}. Package: ${job.package}+ LPA. Location: ${job.location_name}. Apply now!`);
      
      // Set canonical link
      const canonicalUrl = `${window.location.origin}/jobs/${job.job_code}`;
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        canonicalLink.setAttribute('data-dynamic', 'true');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonicalUrl);
      
      // Get company logo or default image
      const companyLogo = job.company_logo || '/logo192.png';
      
      // Set Open Graph tags
      createMeta('og:title', `${job.title} | ${job.company_name}`);
      createMeta('og:description', `${job.title} at ${job.company_name}. Package: ${job.package}+ LPA. Location: ${job.location_name}. Apply now!`);
      createMeta('og:url', canonicalUrl);
      createMeta('og:type', 'website');
      createMeta('og:site_name', 'Fresher Job Adda');
      createMeta('og:image', companyLogo);
      createMeta('og:image:width', '1200');
      createMeta('og:image:height', '630');
      createMeta('og:locale', 'en_IN');
      
      // Set Twitter Card tags
      createMeta('twitter:card', 'summary_large_image');
      createMeta('twitter:title', `${job.title} | ${job.company_name}`);
      createMeta('twitter:description', `${job.title} at ${job.company_name}. Package: ${job.package}+ LPA. Location: ${job.location_name}. Apply now!`);
      createMeta('twitter:image', companyLogo);
      
      // Set job-specific meta tags
      createMeta('keywords', `jobs, career, ${job.category_name}, ${job.company_name}, ${job.title}, ${job.location_name}, ${job.job_code}`);
      createMeta('robots', 'index, follow');
      
      createMeta('job:title', job.title);
      createMeta('job:company', job.company_name);
      createMeta('job:location', job.location_name);
      createMeta('job:salary', `${job.package}+ LPA`);
      createMeta('job:code', job.job_code);
      createMeta('job:category', job.category_name);
      createMeta('job:position', job.position);
      
      // Add structured data
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        "title": job.title,
        "description": job.description || `${job.title} at ${job.company_name}. Package: ${job.package}+ LPA. Location: ${job.location_name}. Apply now!`,
        "datePosted": new Date().toISOString(),
        "validThrough": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        "employmentType": job.position || 'Full Time',
        "hiringOrganization": {
          "@type": "Organization",
          "name": job.company_name,
          "sameAs": window.location.origin,
          "logo": companyLogo
        },
        "jobLocation": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": job.location_name,
            "addressCountry": "IN"
          }
        },
        "baseSalary": {
          "@type": "MonetaryAmount",
          "currency": "INR",
          "value": {
            "@type": "QuantitativeValue",
            "value": job.package,
            "unitText": "YEAR"
          }
        },
        "identifier": {
          "@type": "PropertyValue",
          "name": "Fresher Job Adda Job Code",
          "value": job.job_code
        }
      };
      
      const scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-dynamic', 'true');
      scriptTag.textContent = JSON.stringify(structuredData);
      document.head.appendChild(scriptTag);
    };

    if (allJobsLoading) return;
    
    if (jobsError) {
      setError('Error loading jobs data');
      setIsLoading(false);
      return;
    }

    if (!allJobs || allJobs.length === 0) {
      setError('No jobs data available');
      setIsLoading(false);
      return;
    }

    // Find job by job code
    const foundJob = allJobs.find(job => 
      job.job_code && job.job_code.toUpperCase() === jobCode.toUpperCase()
    );

    if (foundJob) {
      console.log('Found job with code:', foundJob);
      setJobData(foundJob);
      
      // Add meta tags directly to the document head
      addMetaTags(foundJob);
    } else {
      setError('Job not found');
    }

    setIsLoading(false);
    
    // Cleanup function to remove meta tags when component unmounts
    return () => {
      document.querySelectorAll('meta[data-dynamic="true"]').forEach(el => el.remove());
      document.querySelectorAll('script[data-dynamic="true"]').forEach(el => el.remove());
    };
  }, [allJobs, allJobsLoading, jobCode, jobsError, navigate]);

  // Helper function to create slugs
  const slugify = (str) =>
    str?.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '') || '';

  // Generate proper meta description for sharing
  const generateMetaDescription = () => {
    if (!jobData) return 'Job details on Fresher Job Adda';
    
    return `${jobData.title} at ${jobData.company_name}. Package: ${jobData.package}+ LPA. Location: ${jobData.location_name}. Apply now!`;
  };

  // Generate canonical URL
  const getCanonicalUrl = () => {
    if (!jobData) return window.location.href;
    
    // If we have a job code, use that for the canonical URL
    if (jobData.job_code) {
      return `${window.location.origin}/jobs/${jobData.job_code}`;
    }
    
    // Fallback to slugified title
    const formattedTitle = slugify(jobData.title);
    return `${window.location.origin}/jobs/${formattedTitle}`;
  };

  // Get the company logo or default image
  const getCompanyLogo = () => {
    if (!jobData) return '/logo192.png';
    return jobData.company_logo || '/logo192.png';
  };

  // Generate structured data
  const getStructuredData = () => {
    if (!jobData) return {};
    
    const siteUrl = window.location.origin;
    
    return {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": jobData.title,
      "description": jobData.description || generateMetaDescription(),
      "datePosted": new Date().toISOString(),
      "validThrough": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      "employmentType": jobData.position || 'Full Time',
      "hiringOrganization": {
        "@type": "Organization",
        "name": jobData.company_name,
        "sameAs": siteUrl,
        "logo": getCompanyLogo()
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": jobData.location_name,
          "addressCountry": "IN"
        }
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "INR",
        "value": {
          "@type": "QuantitativeValue",
          "value": jobData.package,
          "unitText": "YEAR"
        }
      },
      "identifier": {
        "@type": "PropertyValue",
        "name": "Fresher Job Adda Job Code",
        "value": jobData.job_code
      }
    };
  };

  // Continue using Helmet as well for React-based pages
  if (isLoading) {
    return (
      <Helmet>
        <title>Loading Job | Fresher Job Adda</title>
        <meta name="description" content="Loading job details" />
      </Helmet>
    );
  }

  if (error || !jobData) {
    return (
      <Helmet>
        <title>Job Not Found | Fresher Job Adda</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="The requested job could not be found" />
        <meta property="og:image" content="/logo192.png" />
        <meta name="twitter:image" content="/logo192.png" />
      </Helmet>
    );
  }

  return (
    <Helmet>
      <title>{jobData.title} | {jobData.company_name} | Fresher Job Adda</title>
      
      {/* Basic meta tags */}
      <meta name="description" content={generateMetaDescription()} />
      <link rel="canonical" href={getCanonicalUrl()} />
      
      {/* Open Graph tags for Facebook, LinkedIn */}
      <meta property="og:title" content={`${jobData.title} | ${jobData.company_name}`} />
      <meta property="og:description" content={generateMetaDescription()} />
      <meta property="og:url" content={getCanonicalUrl()} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Fresher Job Adda" />
      <meta property="og:image" content={getCompanyLogo()} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${jobData.title} | ${jobData.company_name}`} />
      <meta name="twitter:description" content={generateMetaDescription()} />
      <meta name="twitter:image" content={getCompanyLogo()} />
      
      {/* Additional job-specific metadata */}
      <meta name="keywords" content={`jobs, career, ${jobData.category_name}, ${jobData.company_name}, ${jobData.title}, ${jobData.location_name}, ${jobData.job_code}`} />
      <meta name="robots" content="index, follow" />
      
      {/* Job specific meta tags */}
      <meta name="job:title" content={jobData.title} />
      <meta name="job:company" content={jobData.company_name} />
      <meta name="job:location" content={jobData.location_name} />
      <meta name="job:salary" content={`${jobData.package}+ LPA`} />
      <meta name="job:code" content={jobData.job_code} />
      <meta name="job:category" content={jobData.category_name} />
      <meta name="job:position" content={jobData.position} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData())}
      </script>
    </Helmet>
  );
};

export default JobMeta; 